
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { ModelProvider, AIModel } from "@/types/admin";

// Fetch model providers
export const fetchModelProviders = async (): Promise<ModelProvider[]> => {
  try {
    // In a real implementation, this would be fetched from a database
    // For now, we'll return a static list of providers
    return [
      {
        id: "openai",
        name: "OpenAI",
        isConnected: false,
        authType: "api_key",
        logo_url: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/ChatGPT_logo.svg/120px-ChatGPT_logo.svg.png",
        website: "https://openai.com/"
      },
      {
        id: "anthropic",
        name: "Anthropic",
        isConnected: true,
        authType: "api_key",
        logo_url: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Anthropic_Logo.svg/120px-Anthropic_Logo.svg.png",
        website: "https://www.anthropic.com/"
      },
      {
        id: "google",
        name: "Google AI",
        isConnected: false,
        authType: "api_key",
        logo_url: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/120px-Google_%22G%22_logo.svg.png",
        website: "https://ai.google.dev/"
      }
    ];
  } catch (error) {
    console.error("Error fetching model providers:", error);
    return [];
  }
};

// Fetch AI models
export const fetchAIModels = async (): Promise<AIModel[]> => {
  try {
    // In a real implementation, this would be fetched from a database
    // For now, we'll return a static list of models
    return [
      {
        id: "gpt-4",
        name: "GPT-4",
        provider: "openai",
        max_tokens: 8192,
        capabilities: ["text-generation", "reasoning", "content-summarization"]
      },
      {
        id: "gpt-3.5-turbo",
        name: "GPT-3.5 Turbo",
        provider: "openai",
        max_tokens: 4096,
        capabilities: ["text-generation", "content-summarization"]
      },
      {
        id: "claude-v2",
        name: "Claude 2",
        provider: "anthropic",
        max_tokens: 100000,
        capabilities: ["text-generation", "reasoning", "content-summarization"]
      },
      {
        id: "claude-instant",
        name: "Claude Instant",
        provider: "anthropic",
        max_tokens: 100000,
        capabilities: ["text-generation", "content-summarization"]
      },
      {
        id: "gemini-pro",
        name: "Gemini Pro",
        provider: "google",
        max_tokens: 32768,
        capabilities: ["text-generation", "reasoning", "content-summarization"]
      },
      {
        id: "gemini-ultra",
        name: "Gemini Ultra",
        provider: "google",
        max_tokens: 32768,
        capabilities: ["text-generation", "reasoning", "content-summarization"]
      }
    ];
  } catch (error) {
    console.error("Error fetching AI models:", error);
    return [];
  }
};

// Added the missing functions for AIModelConfig component
export const getSupportedProviders = async (): Promise<ModelProvider[]> => {
  return fetchModelProviders();
};

export const getModelsForProvider = async (providerId: string): Promise<{ models: AIModel[] }> => {
  const allModels = await fetchAIModels();
  const filteredModels = allModels.filter(model => model.provider === providerId);
  return { models: filteredModels };
};

// Set API key for a provider
export const setProviderApiKey = async (providerId: string, apiKey: string): Promise<{success: boolean; error?: string}> => {
  try {
    const { data, error } = await supabase.functions.invoke("set-provider-api-key", {
      body: { provider: providerId, api_key: apiKey }
    });

    if (error) {
      console.error("Error setting provider API key:", error);
      return { success: false, error: error.message };
    }

    return { success: data?.success || false, error: data?.error };
  } catch (error) {
    console.error("Error setting provider API key:", error);
    return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
  }
};

// Check connection to provider
export const checkProviderConnection = async (providerId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke("check-provider-connection", {
      body: { provider: providerId }
    });

    if (error) {
      console.error("Error checking provider connection:", error);
      return false;
    }

    return data?.success || false;
  } catch (error) {
    console.error("Error checking provider connection:", error);
    return false;
  }
};
